<template>
  <div class="gsl-hrm-background-container">
    <oxd-table-filter :filter-title="$t('general.punctuality_report_filters')">
      <oxd-form @submit-valid="filterItems" @reset="onReset">
        <oxd-form-row>
          <oxd-grid :cols="4" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete v-model="filters.employee" />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.month"
                type="select"
                :options="months"
                :label="$t('leave.month')"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.year"
                type="select"
                :options="years"
                :label="$t('leave.year')"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-actions>
          <oxd-button
            type="reset"
            display-type="ghost"
            :label="$t('general.reset')"
          />
          <submit-button :label="$t('general.search')" />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>

    <br />
    <div class="gsl-hrm-paper-container">
      <div class="gsl-hrm-header-container"></div>
      <!-- Table for Employee Attendance -->
      <oxd-divider />
      <div class="gsl-hrm-timesheet-body">
        <table :class="tableClasses">
          <thead class="gsl-hrm-timesheet-table-header">
            <tr class="gsl-hrm-timesheet-table-header-row">
              <th :class="fixedColumnClasses">
                {{ $t('general.employee_name') }}
              </th>
              <th :class="fixedColumnClasses">Punctuality Index</th>
              <th
                v-for="(day, index) in daysOfMonth"
                :key="index"
                :class="[
                  'gsl-hrm-timesheet-table-header-cell',
                  '--center',
                  {'--weekend-column': isWeekend(day.formattedDate)}, // Apply the weekend class conditionally
                ]"
              >
                <span>
                  {{ day.dayName }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody class="gsl-hrm-timesheet-table-body">
            <tr
              v-for="(item, index) in items.data"
              :key="index"
              class="gsl-hrm-timesheet-table-body-row"
            >
              <td :class="fixedCellClasses">
                <span>
                  {{ item.employeeName }}
                </span>
              </td>
              <td :class="[fixedCellClasses, '--center']">
                <span>
                  {{
                    totalDays > 0
                      ? (
                          (Number(item.items.punctual) / totalDays) *
                          100
                        ).toFixed(2)
                      : 0
                  }}%
                </span>
              </td>

              <td
                v-for="(day, i) in daysOfMonth"
                :key="i"
                v-tooltip="day.formattedDate"
                :class="[
                  'gsl-hrm-timesheet-table-body-cell',
                  '--center',
                  {'--weekend-column': isWeekend(day.formattedDate)}, // Apply the weekend class to table body cells
                ]"
              >
                <span
                  :class="[
                    '--bold',
                    {
                      '--amber':
                        item.items[day.formattedDate] === 'PL' ||
                        item.items[day.formattedDate] === 'HL',
                    },
                    {'--green': item.items[day.formattedDate] === 'PP'},
                    {
                      '--red':
                        !item.items[day.formattedDate] &&
                        !isWeekend(day.formattedDate),
                    },
                  ]"
                >
                  {{
                    item.items[day.formattedDate]
                      ? item.items[day.formattedDate]
                      : isWeekend(day.formattedDate)
                      ? '-'
                      : 'UL'
                  }}
                </span>
              </td>
            </tr>
            <tr
              v-if="items?.data?.length === 0"
              class="gsl-hrm-timesheet-table-body-row"
            >
              <td colspan="33" class="gsl-hrm-timesheet-table-body-cell">
                {{ $t('general.no_records_found') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="gsl-hrm-timesheet-footer">
        <div class="gsl-hrm-timesheet-footer--title"></div>
        <div class="gsl-hrm-timesheet-footer--options">
          <oxd-text type="subtitle-2">
            <strong>PP</strong> - Present Punctual <br />
            <strong>UL</strong> - Unplanned Leave <br />
            <strong>PL</strong> - Planned Leave <br />
            <strong>LT</strong> - Late <br />
            <strong>HL</strong> - Holiday
          </oxd-text>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {computed, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import useLocale from '@/core/util/composable/useLocale';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import tooltipDirective from './tooltipDirective';

const currentDate = new Date(); // Get the current date
const defaultFilters = {
  employee: null,
  month: {id: currentDate.getMonth() + 1}, // getMonth() returns 0-based index, so add 1 for the correct month
  year: {id: currentDate.getFullYear()}, // getFullYear() returns the current year
};

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },
  directives: {
    tooltip: tooltipDirective,
  },
  setup() {
    const filters = ref({...defaultFilters});
    const {locale} = useLocale();

    const daysOfMonth = ref([]);
    const totalDays = ref(0);

    // Method to calculate the days of the selected month and year
    const calculateDaysOfMonth = (month, year) => {
      daysOfMonth.value = [];
      const lastDayOfMonth = new Date(year.id, month.id, 0).getDate();
      for (let i = 1; i <= lastDayOfMonth; i++) {
        const date = new Date(year.id, month.id - 1, i); // months are zero-based
        const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        const dayName =
          date.toLocaleString('en-us', {weekday: 'short'}) + ' ' + i;
        daysOfMonth.value.push({formattedDate, dayName});
      }
    };

    const calculateTotalDaysWithoutWeekends = (month, year) => {
      const firstDay = new Date(year.id, month.id - 1, 1);
      const lastDay = new Date(year.id, month.id, 0);
      let daysWithoutWeekends = 0;

      for (let day = firstDay.getDate(); day <= lastDay.getDate(); day++) {
        const date = new Date(year.id, month.id - 1, day);
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          daysWithoutWeekends++;
        }
      }

      totalDays.value = daysWithoutWeekends;
    };

    const punctualityDataNormalizer = (data) => {
      return Object.entries(data[0]).map(([employeeName, items]) => {
        return {
          employeeName,
          items,
        };
      });
    };

    const serializedFilters = computed(() => {
      return {
        empNumber: filters.value.employee?.id,
        month: filters.value.month?.id,
        year: filters.value.year?.id,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/employees/punctuality/report',
    );

    const {response, execQuery} = usePaginate(http, {
      query: serializedFilters,
      normalizer: punctualityDataNormalizer,
    });

    return {
      filters,
      daysOfMonth,
      totalDays,
      calculateTotalDaysWithoutWeekends,
      calculateDaysOfMonth,
      locale,
      execQuery,
      items: response,
    };
  },

  computed: {
    months() {
      return Array(12)
        .fill('')
        .map((...[, index]) => {
          return {
            id: index + 1,
            label: this.locale.localize.month(index, {
              width: 'wide',
            }),
          };
        });
    },
    years() {
      const currentYear = new Date().getFullYear();
      return Array.from({length: 11}, (_, i) => ({
        id: currentYear - i,
        label: `${currentYear - i}`,
      }));
    },
    tableClasses() {
      return {
        'gsl-hrm-timesheet-table': true,
        '--editable': this.editable,
      };
    },
    fixedColumnClasses() {
      return {
        'gsl-hrm-timesheet-table-header-cell': true,
        '--freeze-left': !this.editable,
      };
    },
    fixedCellClasses() {
      return {
        'gsl-hrm-timesheet-table-body-cell': true,
        '--freeze-left': !this.editable,
      };
    },
  },
  beforeMount() {
    this.isLoading = true;
    const currentDate = new Date();
    this.filters.month = this.months[currentDate.getMonth()]; // Get current month
    this.filters.year = this.years[0]; // Get the current year from the years array
    this.calculateDaysOfMonth(this.filters.month, this.filters.year); // Calculate days for the current month and year
    this.calculateTotalDaysWithoutWeekends(
      this.filters.month,
      this.filters.year,
    ); // Calculate days for the current month and year
  },
  methods: {
    async filterItems() {
      if (this.filters.month && this.filters.year) {
        this.calculateDaysOfMonth(this.filters.month, this.filters.year);
        this.calculateTotalDaysWithoutWeekends(
          this.filters.month,
          this.filters.year,
        );
      }
      await this.execQuery();
    },
    onReset() {
      this.filters = {...defaultFilters};
      this.filterItems();
    },
    isWeekend(dateString) {
      const date = new Date(dateString);
      const dayOfWeek = date.getDay();
      // 0 = Sunday, 6 = Saturday
      return dayOfWeek === 0 || dayOfWeek === 6;
    },
  },
};
</script>

<style src="../../components/timesheet.scss" lang="scss" scoped></style>
